// Free-text input with a custom suggestion dropdown — replaces the native `<input list> +
// <datalist>` pattern, which Safari (desktop and iOS) never renders any UI for at all (a
// long-standing WebKit gap), silently turning "autocomplete" into a dead feature for Safari users.
// Unlike SearchableSelect, the visible input IS the real editable field (not a locked choice) —
// typing anything is always accepted; suggestions are just a shortcut, matching the existing
// free-text-plus-suggestions convention this app already relies on for lot-code entry.
import React, { useState, useRef, useEffect } from 'react';

export default function AutocompleteInput({
  value,
  onChange,
  options = [], // [{ value, hint? }]
  placeholder = '',
  className = 'form-control form-control-sm',
  style,
  inputStyle,
  onFocus,
}) {
  const [open, setOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const containerRef = useRef(null);

  const query = (value || '').toLowerCase();
  const filtered = query
    ? options.filter(o => String(o.value).toLowerCase().includes(query))
    : options;

  const computeRect = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    // iOS Safari doesn't shrink window.innerHeight for the on-screen keyboard, but
    // visualViewport.height does — without it, "space below" looks falsely large and the dropdown
    // can end up positioned as if the keyboard weren't there at all.
    const vv = window.visualViewport;
    const viewportTop = vv ? vv.offsetTop : 0;
    const viewportBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const spaceBelow = viewportBottom - rect.bottom;
    const spaceAbove = rect.top - viewportTop;
    const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    setDropdownRect({
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.width,
      openUp,
      maxListHeight: Math.max(100, Math.min(220, (openUp ? spaceAbove : spaceBelow) - 12)),
    });
  };

  useEffect(() => {
    if (!open) return;
    // Focusing an input on iOS kicks off an async "scroll this above the keyboard" animation —
    // computing immediately captures the input's PRE-scroll position, stranding the dropdown far
    // from where the input actually ends up (this was the reported bug). visualViewport's own
    // resize/scroll events fire as that animation plays out, so re-computing on them converges on
    // the right spot instead of guessing a fixed delay.
    const raf = requestAnimationFrame(computeRect);
    const safetyTimeout = setTimeout(computeRect, 350);
    const vv = window.visualViewport;
    window.addEventListener('scroll', computeRect, true);
    window.addEventListener('resize', computeRect);
    vv?.addEventListener('resize', computeRect);
    vv?.addEventListener('scroll', computeRect);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(safetyTimeout);
      window.removeEventListener('scroll', computeRect, true);
      window.removeEventListener('resize', computeRect);
      vv?.removeEventListener('resize', computeRect);
      vv?.removeEventListener('scroll', computeRect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      <input
        type="text"
        className={className}
        style={inputStyle}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => { setOpen(true); onFocus?.(); }}
      />
      {open && dropdownRect && filtered.length > 0 && (
        <div style={{
          position: 'fixed',
          ...(dropdownRect.openUp
            ? { bottom: window.innerHeight - dropdownRect.top + 2 }
            : { top: dropdownRect.bottom + 2 }),
          left: dropdownRect.left,
          width: Math.max(dropdownRect.width, 200),
          zIndex: 9999,
          background: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          maxHeight: dropdownRect.maxListHeight,
          overflowY: 'auto',
        }}>
          {filtered.map(opt => (
            <div
              key={opt.value}
              onMouseDown={() => { onChange(opt.value); setOpen(false); }}
              style={{ padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}
            >
              <strong>{opt.value}</strong>
              {opt.hint && <span className="text-muted ms-2" style={{ fontSize: 11 }}>{opt.hint}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
